import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import BentoCard from '../ui/BentoCard'
import MaterialIcon from '../ui/MaterialIcon'
import SlidePagePanel from '../mobile/SlidePagePanel'
import CoachDashboard from './CoachDashboard'
import { KILO } from '../../constants/KILO_THEME'
import { getTodayDayId } from '../../lib/days'
import { estimateBlockMinutes } from '../../lib/loadProtocol'
import type { CycleProfile } from '../../lib/cycle'
import { MATERIAL_ICONS } from '../../lib/materialIcons'
import { isRestDayBlock } from '../../lib/trainingSchedule'
import type { GroceryItem, MealBlock, TrainingBlock } from '../../lib/protocolTypes'

type CoachWorkoutSectionProps = {
  training: TrainingBlock[]
  isPremium: boolean
  canWeeklyCalendarView?: boolean
  hasWearableConnected?: boolean
  userId?: string | null
  cycleProfile?: CycleProfile | null
  calendarSyncEnabled?: boolean
  dietaryLabel?: string
  meals?: MealBlock[]
  grocery?: GroceryItem[]
  showLiveSession?: boolean
  onLiveSession?: () => void
  onOpenWeeklyView?: () => void
  onOpenShopping?: () => void
  onOpenMeals?: () => void
  onConnectCalendar?: () => void
  onWorkoutUpdated?: () => void
}

const CoachWorkoutSection = ({
  training,
  showLiveSession = false,
  onLiveSession,
  onOpenWeeklyView,
  ...dashboardProps
}: CoachWorkoutSectionProps) => {
  const { t } = useTranslation()
  const [panelOpen, setPanelOpen] = useState(false)

  const handleOpenWeeklyView = () => {
    setPanelOpen(false)
    window.setTimeout(() => onOpenWeeklyView?.(), 320)
  }

  const today = getTodayDayId()
  const todayBlock = useMemo(
    () => training.find((block) => block.day_name_id === today) ?? null,
    [training, today],
  )
  const isRestDay = todayBlock != null && isRestDayBlock(todayBlock)

  const icon = isRestDay ? MATERIAL_ICONS.selfImprovement : MATERIAL_ICONS.sportsMartialArts

  const teaser = useMemo(() => {
    if (training.length === 0) return t('coach.dashboard.programmePending')
    if (!todayBlock) return t('coach.dashboard.noBlockResolved')
    if (isRestDay) {
      return todayBlock.exercises.length > 0
        ? t('coach.dashboard.activeRestDay')
        : t('coach.dashboard.coachRecoveryDay')
    }
    const title = todayBlock.block_title?.trim()
    const subtitle = todayBlock.focus_subtitle?.trim()
    if (title && subtitle) return `${title} · ${subtitle}`
    if (title) return title
    const mins = estimateBlockMinutes(todayBlock)
    return t('coach.sections.workoutTeaser', {
      count: todayBlock.exercises.length,
      mins,
    })
  }, [training.length, todayBlock, isRestDay, t])

  const title = t('coach.sections.workout')

  return (
    <>
      <BentoCard gradient="none" className="!p-0 overflow-hidden">
        <button
          type="button"
          onClick={() => setPanelOpen(true)}
          className="flex w-full items-center gap-3 px-5 py-4 text-left transition-colors hover:bg-kilo-text/[0.02]"
          aria-expanded={panelOpen}
        >
          <MaterialIcon name={icon} hoverable size="sm" className="shrink-0" />
          <div className="min-w-0 flex-1">
            <p className={KILO.meta}>{title}</p>
            <p className={`mt-1 line-clamp-2 text-sm leading-relaxed ${KILO.text.muted}`}>{teaser}</p>
          </div>
          <MaterialIcon name="chevron_right" size="sm" className="shrink-0" />
        </button>
      </BentoCard>

      <SlidePagePanel open={panelOpen} onClose={() => setPanelOpen(false)} title={title}>
        <CoachDashboard
          {...dashboardProps}
          training={training}
          embedded
          showLiveSession={showLiveSession}
          onLiveSession={onLiveSession}
          onOpenWeeklyView={onOpenWeeklyView ? handleOpenWeeklyView : undefined}
        />
      </SlidePagePanel>
    </>
  )
}

export default CoachWorkoutSection
